import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Mail, KeyRound, ShieldCheck, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { authApi } from "@/lib/api";

export const Route = createFileRoute("/_auth/forgot-password")({
  component: ForgotPasswordPage,
});

type Step = 1 | 2 | 3;

const STEPS = [
  { n: 1, label: "Verify email", icon: Mail },
  { n: 2, label: "Enter code", icon: KeyRound },
  { n: 3, label: "New password", icon: ShieldCheck },
] as const;

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      toast.success(`Verification code sent to ${email}`);
      setStep(2);
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Failed to send code";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    setLoading(true);
    try {
      await authApi.verifyResetOtp({ email: email.trim(), code: otp });
      setStep(3);
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Invalid code";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await authApi.resetPassword({ email: email.trim(), code: otp, newPassword: password });
      toast.success("Password reset successful. Please sign in.");
      navigate({ to: "/login", search: { redirect: "/" } });
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Failed to reset password";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto flex min-h-[calc(100vh-100px)] max-w-md flex-col justify-center px-4 py-4 sm:py-6">
      <div className="w-full rounded-2xl border border-border/60 bg-card p-8 shadow-elegant">
        <h1 className="font-display text-3xl font-bold">Reset your password</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Follow the 3 steps below to recover access to your account.
        </p>

        {/* Stepper */}
        <ol className="mt-8 flex items-center justify-between">
          {STEPS.map((s, i) => {
            const active = step === s.n;
            const done = step > s.n;
            const Icon = done ? Check : s.icon;
            return (
              <li key={s.n} className="flex flex-1 items-center">
                <div className="flex flex-col items-center gap-2">
                  <div
                    className={`grid size-10 place-items-center rounded-full border-2 transition-colors ${
                      done
                        ? "border-primary bg-primary text-primary-foreground"
                        : active
                          ? "border-primary text-primary"
                          : "border-border text-muted-foreground"
                    }`}
                  >
                    <Icon className="size-4" />
                  </div>
                  <span className={`text-[11px] font-medium ${active || done ? "text-foreground" : "text-muted-foreground"}`}>
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`mx-2 mb-6 h-0.5 flex-1 ${step > s.n ? "bg-primary" : "bg-border"}`} />
                )}
              </li>
            );
          })}
        </ol>

        <div className="mt-8">
          {step === 1 && (
            <form onSubmit={sendCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
                <p className="text-xs text-muted-foreground">
                  We'll send a 6-digit verification code to this address.
                </p>
              </div>
              <Button type="submit" disabled={loading} size="lg" className="w-full">
                {loading ? <Loader2 className="size-4 animate-spin" /> : "Send verification code"}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyCode} className="space-y-5">
              <div className="space-y-2">
                <Label>Enter the 6-digit code</Label>
                <p className="text-xs text-muted-foreground">
                  Sent to <span className="font-medium text-foreground">{email}</span>
                </p>
                <div className="flex justify-center pt-2">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} className="size-11 text-base" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              <Button type="submit" disabled={loading || otp.length !== 6} size="lg" className="w-full">
                {loading ? <Loader2 className="size-4 animate-spin" /> : "Verify code"}
              </Button>
              <button
                type="button"
                className="block w-full text-center text-xs text-muted-foreground hover:text-primary"
                onClick={() => { setOtp(""); setStep(1); }}
              >
                Use a different email
              </button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={resetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={loading} size="lg" className="w-full">
                {loading ? <Loader2 className="size-4 animate-spin" /> : "Reset password"}
              </Button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Remembered it?{" "}
          <Link to="/login" search={{ redirect: "/" }} className="font-semibold text-primary hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </section>
  );
}
